import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { useAppSelector } from "../../store";
import ThemeToggle from "../common/ThemeToggle";
import LanguageToggle from "../common/LanguageToggle";

interface HeaderProps {
  onMobileMenuToggle?: () => void;
  isMobile?: boolean;
  sidebarOpen?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onMobileMenuToggle,
  isMobile = false,
  sidebarOpen = false,
}) => {
  const { theme } = useTheme();
  const { user } = useAppSelector((state) => state.auth);
  const isDark = theme === "dark";

  const headerStyle: React.CSSProperties = {
    background: isDark ? "rgba(26, 32, 44, 0.95)" : "rgba(255, 255, 255, 0.95)",
    borderBottom: isDark
      ? "1px solid rgba(255, 255, 255, 0.08)"
      : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: isDark
      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
    backdropFilter: "blur(20px)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "sticky",
    top: 0,
    zIndex: 100,
    height: "80px",
  };

  const mobileMenuButtonStyle: React.CSSProperties = {
    background: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
    border: "none",
    borderRadius: "12px",
    width: "44px",
    height: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: isDark ? "#e2e8f0" : "#4a5568",
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontSize: "20px",
  };

  const userInfoStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.5rem 1rem",
    background: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
    borderRadius: "12px",
    transition: "all 0.3s ease",
  };

  const avatarStyle: React.CSSProperties = {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: isDark
      ? "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)"
      : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: isDark
      ? "0 4px 12px rgba(59, 130, 246, 0.3)"
      : "0 4px 12px rgba(59, 130, 246, 0.2)",
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header style={headerStyle}>
      <div
        style={{
          height: "100%",
          padding: "0 1.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left Section - Mobile Menu & Title */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={onMobileMenuToggle}
              style={mobileMenuButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDark
                  ? "rgba(255, 255, 255, 0.15)"
                  : "rgba(0, 0, 0, 0.08)";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.05)";
                e.currentTarget.style.transform = "scale(1)";
              }}
              aria-label="Toggle mobile menu"
            >
              <i
                className={`bi ${sidebarOpen ? "bi-x" : "bi-list"}`}
                style={{
                  transition: "all 0.3s ease",
                  transform: sidebarOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>
          )}

          {/* Page Title - Hidden on small mobile */}
          <div
            style={{
              display: isMobile ? "none" : "block",
              color: isDark ? "#f1f5f9" : "#1e293b",
              fontSize: "1.25rem",
              fontWeight: "600",
              letterSpacing: "-0.025em",
            }}
          >
            Dashboard
          </div>
        </div>

        {/* Right Section - User Info & Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {/* User Info - Hidden on small mobile */}
          {!isMobile && user && (
            <div style={userInfoStyle}>
              <div style={avatarStyle}>
                {getInitials(user.full_name || "User")}
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    lineHeight: "1.2",
                  }}
                >
                  {user.full_name || "User"}
                </span>
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: isDark ? "#94a3b8" : "#64748b",
                    lineHeight: "1.2",
                  }}
                >
                  {user.roles?.[0] || "User"}
                </span>
              </div>
            </div>
          )}

          {/* Controls Container */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? "0.5rem" : "1rem",
              padding: "0.5rem",
              background: isDark
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.03)",
              borderRadius: "12px",
            }}
          >
            {/* Language Toggle */}
            <div style={{ transform: isMobile ? "scale(0.9)" : "scale(1)" }}>
              <LanguageToggle />
            </div>

            {/* Theme Toggle */}
            <div style={{ transform: isMobile ? "scale(0.9)" : "scale(1)" }}>
              <ThemeToggle variant="icon" />
            </div>

            {/* Mobile User Avatar */}
            {isMobile && user && (
              <div
                style={{
                  ...avatarStyle,
                  width: "32px",
                  height: "32px",
                  fontSize: "12px",
                  marginLeft: "0.25rem",
                }}
              >
                {getInitials(user.full_name || "User")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile User Info Bar */}
      {isMobile && user && (
        <div
          style={{
            padding: "0.75rem 1.5rem",
            background: isDark
              ? "rgba(255, 255, 255, 0.03)"
              : "rgba(0, 0, 0, 0.02)",
            borderTop: isDark
              ? "1px solid rgba(255, 255, 255, 0.05)"
              : "1px solid rgba(0, 0, 0, 0.05)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            <div style={avatarStyle}>
              {getInitials(user.full_name || "User")}
            </div>
            <div>
              <div
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  color: isDark ? "#f1f5f9" : "#1e293b",
                  lineHeight: "1.2",
                }}
              >
                {user.full_name || "User"}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: isDark ? "#94a3b8" : "#64748b",
                  lineHeight: "1.2",
                }}
              >
                {user.roles?.[0] || "User"}
              </div>
            </div>
          </div>

          {/* Status Indicator */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: "0.75rem",
              color: isDark ? "#94a3b8" : "#64748b",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#10b981",
                boxShadow: "0 0 8px rgba(16, 185, 129, 0.4)",
              }}
            />
            Online
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
